import { ReactNode } from 'react';
import draftToHtml from 'draftjs-to-html';
import pluralize from 'pluralize';

import {
  AUDIO,
  DOCUMENT,
  EBOOK_INTERACTIVE,
  EBOOK_PDF,
  IMAGE,
  INTERACTIVE,
  VIDEO,
} from 'global/constants';
import { InquisitiveStimulusResourceType } from 'global/graphql/scalars.type';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import InlineSvg from 'approot/shared/inline-svg/inline-svg';
import ImageThumbnail, { ImageStatic } from 'approot/shared/images/images';
import { responsiveImageUrl } from 'approot/shared/images/imgix-responsive';
import MediaPlayer from 'approot/video/player';
import type { SearchStimulusResourcesQuery } from '../stimulus.graphql.generated';
import type { StimulusResourceFragment } from '../stimulus.graphql.generated';
import * as Styled from 'approot/shared/stimulus/search/stimulus-search-item.style';
import StimulusPagesPreview from './stimulus-pages-preview';
import { getRegionalisedLink } from 'approot/shared/logic/get-regionalised-link';
import IconExpand from 'approot/shared/icons/icon-expand';
import { Box } from 'approot/shared/design-system/box/box.style';
import type { Pluck } from 'lib/gql/pluck';

type Props = {
  stimulus: Pluck<
    SearchStimulusResourcesQuery,
    'searchStimulusResources.stimulusResources'
  >;
  type?: InquisitiveStimulusResourceType;
  action?: ReactNode;
  isActive?: boolean;
  onClick?: () => void;
};

const StimulusSearchItemPreview = ({
  stimulus,
  type,
  action,
  isActive,
  onClick,
}: Props) => {
  const contentMarkup = draftToHtml(stimulus.description);

  const usage = [...stimulus.lessons, ...stimulus.assessments].length;

  return (
    <Styled.StimulusItem $isActive={isActive}>
      {!isActive && (
        <Styled.StimulusItemButton
          type="button"
          data-selector="stimulus-item"
          onClick={onClick}
        >
          <ScreenReaderOnly>
            {stimulus.name}, {stimulus.type}, Used in {usage}{' '}
            {pluralize('lesson', usage)}
          </ScreenReaderOnly>
        </Styled.StimulusItemButton>
      )}
      <Styled.StimulusItemWrapper>
        {!isActive && (
          <Styled.StimulusItemThumbnail aria-hidden $withMediaPreview>
            <ImageThumbnail
              width={96}
              images={stimulus.coverImage?.responsive || []}
            />
            <Styled.StimulusItemIcon>
              <InlineSvg id={stimulus.type} />
            </Styled.StimulusItemIcon>
          </Styled.StimulusItemThumbnail>
        )}
        <Styled.StimulusItemDetails aria-hidden $noPadding={isActive}>
          {isActive && stimulus && (
            <Box $marginBottom={16}>
              {type === VIDEO || type === AUDIO ? (
                <MediaPlayer
                  name={stimulus.name || ''}
                  embed={
                    (
                      stimulus as Extract<
                        StimulusResourceFragment,
                        { __typename: 'StimulusResourceVideo' }
                      >
                    ).videos[0].embed
                  }
                />
              ) : type === IMAGE ? (
                <ImageStatic
                  imageUrl={
                    (
                      stimulus as Extract<
                        StimulusResourceFragment,
                        { __typename: 'StimulusResourceImage' }
                      >
                    )?.image?.original || ''
                  }
                />
              ) : type === DOCUMENT || type === EBOOK_PDF ? (
                <StimulusPagesPreview
                  fileKey={
                    (
                      stimulus as Extract<
                        StimulusResourceFragment,
                        { __typename: 'StimulusResourceDocument' }
                      >
                    ).documentKey
                  }
                />
              ) : (
                <ImageStatic
                  imageUrl={
                    responsiveImageUrl(
                      stimulus.coverImage?.responsive || [],
                      300,
                      undefined,
                      undefined
                    ) || ''
                  }
                />
              )}
            </Box>
          )}
          {isActive && (type === INTERACTIVE || type === EBOOK_INTERACTIVE) ? (
            <Styled.StimulusItemTitleLink
              href={getRegionalisedLink(stimulus.slug || '')}
              target="_blank"
            >
              {stimulus.name}
              <IconExpand width={16} height={16} />
            </Styled.StimulusItemTitleLink>
          ) : (
            <Styled.StimulusItemTitle>{stimulus.name}</Styled.StimulusItemTitle>
          )}

          <Styled.StimulusItemText
            dangerouslySetInnerHTML={{ __html: contentMarkup }}
          ></Styled.StimulusItemText>
        </Styled.StimulusItemDetails>
        {action && (
          <Styled.StimulusItemActions>{action}</Styled.StimulusItemActions>
        )}
      </Styled.StimulusItemWrapper>
    </Styled.StimulusItem>
  );
};

export default StimulusSearchItemPreview;
